/**
 * The one toast stack (BS Labs "Alert, toast and tooltip").
 *
 * Bottom left (full width on phones), one at a time: a new toast replaces the
 * one showing. 5 s each, Undo and link actions included; hovering or focusing
 * keeps it up. Persistent toasts (duration `false`) stay until closed or
 * replaced: keep those for errors the user must act on.
 *
 * State is module level so every caller - the vue3-toastify adapter
 * (`@/utils/toast`), `useToast()` and new code - feeds the single
 * <ToastStack /> mounted in App.vue.
 *
 * Undo: pass `undo: () => restore()`. The stack only renders the button; what
 * Undo actually does is the caller's job.
 */
import { reactive } from 'vue';

export type ToastType = 'success' | 'error' | 'warning' | 'info';
export type ToastId = number | string;

export interface ToastAction {
    label: string;
    onClick: () => void;
}

export interface ToastOptions {
    /** First line, bold. */
    title: string;
    /** Optional second line, secondary colour. */
    description?: string;
    type?: ToastType;
    /** Milliseconds; `false` keeps it until closed. Default follows the spec. */
    duration?: number | false;
    /** Shows an "Undo" action. */
    undo?: () => void;
    /** A link-style action, e.g. "View in Costing" or "Retry". */
    action?: ToastAction;
    /** Reuse an id to replace a toast in place instead of stacking a new one. */
    id?: ToastId;
    /** Called once when the toast leaves, however it leaves. */
    onClose?: () => void;
}

export interface ToastItem {
    id: ToastId;
    type: ToastType;
    title: string;
    description: string;
    actions: ToastAction[];
    duration: number | false;
    /** role="alert" for errors that block work, role="status" otherwise. */
    role: 'alert' | 'status';
}

export const TOAST_MS = 5000;
export const TOAST_UNDO_MS = 5000;
export const TOAST_ACTION_MS = 5000;
export const TOAST_MAX = 1;

interface Timer {
    handle: ReturnType<typeof setTimeout> | null;
    remaining: number;
    startedAt: number;
    onClose?: () => void;
}

const state = reactive<{ toasts: ToastItem[] }>({ toasts: [] });
const timers = new Map<ToastId, Timer>();
let seq = 0;

export function defaultDuration(opts: Pick<ToastOptions, 'undo' | 'action'>): number {
    if (opts.undo) return TOAST_UNDO_MS;
    if (opts.action) return TOAST_ACTION_MS;
    return TOAST_MS;
}

function startTimer(id: ToastId) {
    const t = timers.get(id);
    if (!t || t.handle || t.remaining <= 0 || !Number.isFinite(t.remaining)) return;
    t.startedAt = Date.now();
    t.handle = setTimeout(() => dismissToast(id), t.remaining);
}

export function pauseToast(id: ToastId) {
    const t = timers.get(id);
    if (!t || !t.handle) return;
    clearTimeout(t.handle);
    t.handle = null;
    t.remaining = Math.max(0, t.remaining - (Date.now() - t.startedAt));
}

export function resumeToast(id: ToastId) {
    const t = timers.get(id);
    if (!t) return;
    // Give a hovered toast at least a moment to be read after the pointer leaves.
    if (t.remaining < 1000) t.remaining = 1000;
    startTimer(id);
}

export function dismissToast(id: ToastId) {
    const t = timers.get(id);
    if (t?.handle) clearTimeout(t.handle);
    timers.delete(id);
    const i = state.toasts.findIndex((x) => x.id === id);
    if (i > -1) state.toasts.splice(i, 1);
    if (t?.onClose) {
        try {
            t.onClose();
        } catch (e) {
            console.error('Toast onClose failed:', e);
        }
    }
}

export function clearToasts() {
    [...state.toasts].forEach((x) => dismissToast(x.id));
}

export function pushToast(opts: ToastOptions): ToastId {
    const type: ToastType = opts.type ?? 'success';
    const duration: number | false =
        opts.duration === false ? false : typeof opts.duration === 'number' && opts.duration > 0 ? opts.duration : defaultDuration(opts);

    const actions: ToastAction[] = [];
    if (opts.undo) {
        const undo = opts.undo;
        actions.push({ label: 'Undo', onClick: undo });
    }
    if (opts.action) actions.push(opts.action);

    const item: ToastItem = {
        id: opts.id ?? `t${++seq}`,
        type,
        title: opts.title,
        description: opts.description ?? '',
        actions,
        duration,
        role: type === 'error' && duration === false ? 'alert' : 'status'
    };

    const existing = state.toasts.findIndex((x) => x.id === item.id);
    if (existing > -1) {
        // Replace in place (same id): restart its clock, keep its slot.
        const old = timers.get(item.id);
        if (old?.handle) clearTimeout(old.handle);
        state.toasts.splice(existing, 1, item);
    } else {
        state.toasts.push(item);
        // One at a time: the newest replaces what is showing. (With a higher
        // cap the oldest timed toast would go first, then the oldest persistent.)
        while (state.toasts.length > TOAST_MAX) {
            const victim = state.toasts.find((x) => x.duration !== false && x.id !== item.id) ?? state.toasts[0];
            dismissToast(victim.id);
        }
    }

    timers.set(item.id, {
        handle: null,
        remaining: duration === false ? Infinity : duration,
        startedAt: 0,
        onClose: opts.onClose
    });
    startTimer(item.id);
    return item.id;
}

export function useToastStack() {
    return {
        toasts: state.toasts,
        push: pushToast,
        dismiss: dismissToast,
        pause: pauseToast,
        resume: resumeToast,
        clear: clearToasts
    };
}
